/**
 * Notes Panel V2 Models
 *
 * SCOPE: Currently used for loan notes only.
 * Leads, applications, and partners continue to use V1.
 *
 * These models match the CLEAR2.API LoanNote entity.
 */

/**
 * Tag info attached to a note, returned by the API.
 * Matches CMG.Clear2.Client.Models.LoanNoteTagInfo
 */
export interface NoteTagInfo {
  loanNoteTagGUID: string;
  loanNoteGUID: string;
  tagGUID: string;
  name: string;
  backgroundColor: string;
  textColor: string;
  hoverBackgroundColor?: string;
  hoverTextColor?: string;
}

/**
 * Represents a loan note from the API.
 * Matches CMG.Clear2.Common.Models.LoanNote
 *
 * NOTE: Some fields are optional to support legacy Scratchpad notes from
 * the LoanColorMapping table (dual-table UNION query in MVP 1).
 *
 * Per BRD "Backwards Compatibility - Display Notes":
 * - Legacy notes display Author = "System"
 * - Legacy notes don't have timestamps or GUIDs
 * - Legacy notes don't trigger unread badges
 */
export interface Note {
  /**
   * Unique identifier (GUID).
   * Optional: Legacy notes from LoanColorMapping don't have GUIDs.
   */
  guid?: string;

  /** The loan's ByteFileDataID */
  byteFileDataId: number;

  /** The note content (may contain HTML formatting) */
  content: string;

  /**
   * Tags/categories assigned to this note.
   * Enriched by the API from the LoanNoteTag junction table.
   * Optional: Legacy notes don't have tags.
   */
  tags?: NoteTagInfo[];

  /**
   * Whether this note is pinned (global for all users).
   * Optional: Legacy notes don't support pinning.
   */
  isPinned?: boolean;

  /** When the note was pinned (null if not pinned) */
  pinnedAt?: Date | null;

  /** User GUID who pinned the note (null if not pinned) */
  pinnedBy?: string | null;

  /**
   * When the note was created.
   * Optional: Legacy notes don't have creation timestamps.
   */
  dateInserted?: Date;

  /** User GUID who created the note (used for permission checks) */
  insertedBy: string;

  /**
   * Display name of user who created the note (FirstName + LastName).
   * Optional: Legacy notes don't have this - display "System" instead.
   */
  insertedByName?: string;

  /** When the note was last updated (null if never edited) */
  dateUpdated?: Date | null;

  /** User GUID who last updated the note (null if never edited) */
  updatedBy?: string | null;

  /** Display name of user who last updated the note (null if never edited) */
  updatedByName?: string | null;

  /** Whether this note is unread for the current user */
  isUnread: boolean;

  /**
   * Whether this is a legacy note from the EPS Loan API (LoanColorMapping).
   * Local Clear2 notes are always false. Legacy notes from EPS are true.
   */
  isLegacy: boolean;
}

/**
 * Payload for creating a new note.
 * API: POST /api/v1/loans/{loanId}/notes
 */
export interface CreateNoteRequest {
  content: string;
}

/**
 * Payload for updating an existing note.
 * API: PUT /api/v1/notes/{noteId}
 */
export interface UpdateNoteRequest {
  content: string;
}

/**
 * Payload for setting note pin state.
 * API: PATCH /api/v1/notes/{noteId}/pin
 */
export interface SetNotePinRequest {
  isPinned: boolean;
}

/**
 * Represents an active filter displayed as a removable tag (BRD FR-5.7).
 */
export interface FilterTag {
  /** Display label for the tag */
  label: string;
  /** Filter type for removal logic */
  type: 'myNotes' | 'sharedNotes' | 'user' | 'dateRange' | 'unread' | 'category';
  /** Associated value (e.g., user GUID, category name) */
  value?: string;
  /** Background color for category filter tags */
  backgroundColor?: string;
  /** Text color for category filter tags */
  textColor?: string;
  /** Hover background color for category filter tags */
  hoverBackgroundColor?: string;
  /** Hover text color for category filter tags */
  hoverTextColor?: string;
}

/**
 * Represents a user option for the Users filter dropdown.
 * Derived from notes list (users who have created notes).
 */
export interface NoteUserOption {
  /** User GUID */
  guid: string;
  /** Display name (FirstName LastName) */
  name: string;
}

/**
 * Filter state for notes panel.
 * All filters are applied client-side after fetching notes.
 */
export interface NotesFilter {
  /** Show only notes created by current user (BRD FR-5.2) */
  myNotes: boolean;

  /** Show only unread notes */
  unread: boolean;

  /** Show only shared/legacy notes (from original Scratchpad) */
  sharedNotes: boolean;

  /** Filter by selected user GUIDs (empty = all users) */
  userGuids: string[];

  /** Filter by date range start (null = no start limit) */
  dateFrom: Date | null;

  /** Filter by date range end (null = no end limit) */
  dateTo: Date | null;

  /** Filter by tag GUIDs (category tags) */
  tagGUIDs: string[];
}

/**
 * Display order for LoanNote category tags (matches Figma design).
 * Tags not in this list sort to the end alphabetically.
 * Used by both the editor's tags-chips dropdown and the filter multiSelect.
 */
export const LOAN_NOTE_TAG_ORDER: string[] = [
  'Borrower',
  'Loan',
  'Property',
  'Income',
  'Assets',
  'Credit',
  'REO',
  'Cash To/From',
  'GMI & Military',
  'Other',
];

/**
 * Default/empty filter state.
 */
export const DEFAULT_NOTES_FILTER: NotesFilter = {
  myNotes: false,
  unread: false,
  sharedNotes: false,
  userGuids: [],
  dateFrom: null,
  dateTo: null,
  tagGUIDs: [],
};
